import React, { useEffect, useRef, useState } from 'react'
import Swal from 'sweetalert2'
import apiClient, { API_ENDPOINTS, BASE_URL } from '../../config/api'

const PrintBarcodes = () => {
  const [barcodes, setBarcodes] = useState([])
  const [filteredBarcodes, setFilteredBarcodes] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [regenerating, setRegenerating] = useState(false)
  const [regeneratingId, setRegeneratingId] = useState(null)

  const printAreaRef = useRef(null)

  const fetchBarcodes = async (showLoading = false) => {
    try {
      if (showLoading) {
        setLoading(true)
      }
      const response = await apiClient.get(API_ENDPOINTS.PRODUCT_BARCODES || '/api/products/barcodes/')
      const data = Array.isArray(response.data) ? response.data : []
      setBarcodes(data)
      setFilteredBarcodes(data)
      return data
    } catch (error) {
      console.error('Error fetching barcodes:', error)
      if (showLoading) {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to fetch product bar codes. Please try again later.',
        })
      }
      throw error
    } finally {
      if (showLoading) {
        setLoading(false)
      }
    }
  }

  useEffect(() => {
    fetchBarcodes(true)
  }, [])

  useEffect(() => {
    if (!search) {
      setFilteredBarcodes(barcodes)
      return
    }

    const query = search.toLowerCase()
    setFilteredBarcodes(
      barcodes.filter((item) =>
        (item.product_name || '').toLowerCase().includes(query) ||
        (item.product_sku || '').toLowerCase().includes(query) ||
        (item.barcode || '').toLowerCase().includes(query)
      )
    )
  }, [search, barcodes])

  const getBarcodeImageUrl = (path) => {
    if (!path) return ''
    // If backend returns an absolute URL, use it as-is
    if (path.startsWith('http://') || path.startsWith('https://')) {
      return path
    }
    // Otherwise, build from BASE_URL
    try {
      return new URL(path, BASE_URL).href
    } catch {
      return path
    }
  }

  const handleRegenerateBarcodes = async () => {
    try {
      setRegenerating(true)
      // Send all=true as a query parameter
      await apiClient.post('/api/products/barcodes/regenerate/?all=true')
      // Refresh the barcodes list
      await fetchBarcodes(false)
      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: 'Barcodes regenerated successfully.',
      })
    } catch (error) {
      console.error('Error regenerating barcodes:', error)
      console.error('Error response:', error.response)
      const errorMessage = error.response?.data?.error || 
                          error.response?.data?.message || 
                          error.response?.data?.detail ||
                          `Failed to regenerate barcodes. ${error.response?.status === 403 ? 'You may not have permission to perform this action.' : 'Please try again later.'}`
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: errorMessage,
      })
    } finally {
      setRegenerating(false)
    }
  }

  const handleRegenerateSingleBarcode = async (barcodeItem) => {
    try {
      setRegeneratingId(barcodeItem.id)
      // Send barcode_id as a query parameter
      await apiClient.post(`/api/products/barcodes/regenerate/?barcode_id=${barcodeItem.id}`)
      // Refresh the barcodes list
      await fetchBarcodes(false)
      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: 'Barcode regenerated successfully.',
      })
    } catch (error) {
      console.error('Error regenerating barcode:', error)
      console.error('Error response:', error.response)
      const errorMessage = error.response?.data?.error || 
                          error.response?.data?.message || 
                          error.response?.data?.detail ||
                          `Failed to regenerate barcode. ${error.response?.status === 403 ? 'You may not have permission to perform this action.' : 'Please try again later.'}`
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: errorMessage,
      })
    } finally {
      setRegeneratingId(null)
    }
  }

  const handlePrintSingle = (barcodeItem) => {
    // Create a temporary print area for a single barcode
    const printWindow = window.open('', '_blank')
    if (!printWindow) {
      Swal.fire({
        icon: 'error',
        title: 'Print failed',
        text: 'Please allow pop-ups to print individual barcodes.',
      })
      return
    }

    const barcodeImageUrl = getBarcodeImageUrl(barcodeItem.barcode_image)

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Print Barcode - ${barcodeItem.product_name}</title>
          <style>
            @media print {
              @page {
                size: auto;
                margin: 0;
              }
              body {
                margin: 0;
                padding: 0;
              }
            }
            body {
              font-family: Arial, sans-serif;
              margin: 0;
              padding: 0;
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
              min-height: 100vh;
            }
            .product-name {
              margin: 0 0 10px 0;
              padding: 0;
              font-size: 18px;
              font-weight: 600;
              text-align: center;
            }
            .barcode-image {
              margin: 0;
              padding: 0;
            }
            .barcode-image img {
              width: auto;
              height: auto;
              max-width: none;
              max-height: none;
              display: block;
            }
          </style>
        </head>
        <body>
          ${barcodeImageUrl ? `
            <div class="barcode-image">
              <img src="${barcodeImageUrl}" alt="${barcodeItem.barcode || ''}" />
            </div>
          ` : ''}
          <script>
            window.onload = function() {
              window.print();
              window.onafterprint = function() {
                window.close();
              };
            };
          </script>
        </body>
      </html>
    `)
    printWindow.document.close()
  }

  return (
    <>
      <div className="pagetitle">
        <h1>Print Product Bar Codes</h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item"><a href="/">Home</a></li>
            <li className="breadcrumb-item">Products</li>
            <li className="breadcrumb-item active">Print Bar Codes</li>
          </ol>
        </nav>
      </div>

      <section className="section">
        <div className="row">
          <div className="col-lg-12">
            <div className="card">
              <div className="card-body">
                <div
                  className="d-flex justify-content-between align-items-center flex-wrap mb-3"
                  style={{ paddingBottom: '15px', borderBottom: '2px solid #e9ecef' }}
                >
                  <div className="d-flex flex-column">
                    <h5 className="card-title mb-1">
                      <i className="bi bi-upc-scan me-2" />
                      Print Product Bar Codes
                    </h5>
                    <small className="text-muted">
                      Search and print bar codes for your products.
                    </small>
                  </div>

                  <div className="d-flex align-items-center gap-2">
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      onClick={handleRegenerateBarcodes}
                      disabled={regenerating}
                      style={{ whiteSpace: 'nowrap' }}
                    >
                      {regenerating ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                          Regenerating...
                        </>
                      ) : (
                        <>
                          <i className="bi bi-arrow-clockwise me-2" />
                          Regenerate Barcodes
                        </>
                      )}
                    </button>
                    <div className="input-group input-group-sm" style={{ minWidth: '260px' }}>
                      <span className="input-group-text">
                        <i className="bi bi-search" />
                      </span>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Search by name, SKU or barcode..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                      />
                    </div>
                  </div>
                </div>

                {loading ? (
                  <p>Loading bar codes...</p>
                ) : !barcodes.length ? (
                  <p>No bar codes found.</p>
                ) : (
                  <>
                    <p className="mb-2 text-muted" style={{ fontSize: '0.875rem' }}>
                      Showing <strong>{filteredBarcodes.length}</strong> of{' '}
                      <strong>{barcodes.length}</strong> bar codes.
                    </p>

                    {/* Printable area */}
                    <div
                      ref={printAreaRef}
                      id="barcode-print-area"
                      style={{
                        backgroundColor: '#ffffff',
                        padding: '16px',
                        borderRadius: '8px',
                        border: '1px solid #e9ecef',
                      }}
                    >
                      <div
                        className="row g-3"
                        style={{
                          pageBreakInside: 'avoid',
                        }}
                      >
                        {filteredBarcodes.map((item) => (
                          <div
                            className="col-6 col-md-4 col-lg-3"
                            key={item.id}
                            style={{ pageBreakInside: 'avoid' }}
                          >
                            <div
                              className="barcode-item"
                              style={{
                                border: '1px solid #dee2e6',
                                borderRadius: '6px',
                                padding: '6px 8px',
                                textAlign: 'center',
                                fontSize: '11px',
                                position: 'relative',
                              }}
                            >
                              <div
                                style={{
                                  position: 'absolute',
                                  top: '4px',
                                  right: '4px',
                                  display: 'flex',
                                  gap: '4px',
                                  zIndex: 10,
                                }}
                              >
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-primary"
                                  onClick={() => handlePrintSingle(item)}
                                  style={{
                                    padding: '2px 6px',
                                    fontSize: '10px',
                                    lineHeight: '1.2',
                                  }}
                                  title="Print this barcode"
                                >
                                  <i className="bi bi-printer" />
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-success"
                                  onClick={() => handleRegenerateSingleBarcode(item)}
                                  disabled={regeneratingId === item.id}
                                  style={{
                                    padding: '2px 6px',
                                    fontSize: '10px',
                                    lineHeight: '1.2',
                                  }}
                                  title="Regenerate this barcode"
                                >
                                  {regeneratingId === item.id ? (
                                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" style={{ width: '10px', height: '10px' }} />
                                  ) : (
                                    <i className="bi bi-arrow-clockwise" />
                                  )}
                                </button>
                              </div>
                              <div
                                className="product-name-print"
                                style={{
                                  fontWeight: 600,
                                  marginBottom: '2px',
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  paddingRight: '60px',
                                }}
                                title={item.product_name}
                              >
                                {item.product_name}
                              </div>
                              <div style={{ marginBottom: '2px', color: '#6c757d' }}>
                                {/* SKU: <strong>{item.product_sku || item.barcode}</strong> */}
                              </div>
                              {item.barcode_image && (
                                <div style={{ marginBottom: '2px' }}>
                                  <img
                                    src={getBarcodeImageUrl(item.barcode_image)}
                                    alt={item.barcode}
                                    style={{
                                      maxWidth: '100%',
                                      height: '40px',
                                      objectFit: 'contain',
                                    }}
                                  />
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}

export default PrintBarcodes



